import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import { TabsEnum, transformVehicleTripSegments } from "../components/monitorView/monitorViewUtils";
import { WorkShiftDto } from "../models/workShift/workShift";
import { getVehicleTripSegments } from "../services/vehicleService";
import { VehicleTripSegment } from "../models/vehicle/vehicleTripSegment";
import { VehicleListItemDto } from "../models/transport/vehicle";
import { VehiclePositionInfo } from "../models/vehicle/vehiclePosition";
import { VehicleData } from "../models/monitorView/monitorViewTypes";

interface MonitorViewState {
    groupMapPoints: boolean;
    activeTab: number;
    isWorkShiftView: boolean;
    isVehicleRouteView: boolean;
    selectedVehicleId: string | null;
    selectedEmployeeId: string | null;
    selectedWorkShiftTimeSlotId: string | null;
    selectedWorkShift: WorkShiftDto | null;
    selectedRouteId: string | null;
    hoveredRouteId: string | null;
    sidebarTitle: string;
    vehicleRoute: VehicleTripSegment[] | null;
    mapData: VehicleData[];
    tripStartDate: Date | null;
    tripEndDate: Date | null;
    vehicles: VehicleListItemDto[] | null;
    positions: VehiclePositionInfo[] | null;
    loading: boolean;
    error: string | null;
}

export const initialState: MonitorViewState = {
    groupMapPoints: true,
    activeTab: TabsEnum.VEHICLES,
    isWorkShiftView: false,
    isVehicleRouteView: false,
    selectedVehicleId: null,
    selectedEmployeeId: null,
    selectedWorkShiftTimeSlotId: null,
    selectedWorkShift: null,
    selectedRouteId: null,
    hoveredRouteId: null,
    sidebarTitle: "",
    vehicleRoute: null,
    mapData: null,
    tripStartDate: dayjs().startOf("day").toDate(),
    tripEndDate: dayjs().startOf("day").add(1, "day").toDate(),
    vehicles: null,
    positions: null,
    loading: false,
    error: null,
};

export const fetchVehicleTripSegments = createAsyncThunk(
    "monitorView/fetchVehicleTripSegments",
    async({ vehicleId, startDate, endDate }: { vehicleId: string; startDate: string; endDate: string }, { rejectWithValue }) => {
        try {
            const response = await getVehicleTripSegments(vehicleId, startDate, endDate);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const monitorViewSlice = createSlice({
    name: "monitorView",
    initialState,
    reducers: {
        setGroupMapPoints: (state, action: PayloadAction<boolean>) => {
            state.groupMapPoints = action.payload;
        },
        setActiveTab: (state, action: PayloadAction<number>) => {
            state.activeTab = action.payload;
        },
        setIsWorkShiftView(state, action: PayloadAction<boolean>) {
            state.isWorkShiftView = action.payload;
        },
        toggleWorkShiftView(state) {
            state.isWorkShiftView = !state.isWorkShiftView;
        },
        setIsVehicleRouteView(state, action: PayloadAction<boolean>) {
            state.isVehicleRouteView = action.payload;
        },
        toggleVehicleRouteView(state) {
            state.isVehicleRouteView = !state.isVehicleRouteView;
        },
        setSelectedVehicleId(state, action: PayloadAction<string | null>) {
            state.selectedVehicleId = action.payload;
        },
        setSelectedEmployeeId(state, action: PayloadAction<string | null>) {
            state.selectedEmployeeId = action.payload;
        },
        setSelectedWorkShiftTimeSlotId(state, action: PayloadAction<string | null>) {
            state.selectedWorkShiftTimeSlotId = action.payload;
        },
        setSelectedWorkShift: (state, action: PayloadAction<WorkShiftDto | null>) => {
            state.selectedWorkShift = action.payload;
        },
        setSelectedRouteId(state, action: PayloadAction<string | null>) {
            state.selectedRouteId = action.payload;
        },
        setHoveredRouteId(state, action: PayloadAction<string | null>) {
            state.hoveredRouteId = action.payload;
        },
        setSidebarTitle: (state, action: PayloadAction<string>) => {
            state.sidebarTitle = action.payload;
        },
        setVehicleRoute: (state, action: PayloadAction<VehicleTripSegment[]>) => {
            state.vehicleRoute = action.payload;
        },
        setTripStartDate(state, action: PayloadAction<Date | null>) {
            state.tripStartDate = action.payload;
        },
        setTripEndDate(state, action: PayloadAction<Date | null>) {
            state.tripEndDate = action.payload;
        },
        setVehicles(state, action: PayloadAction<VehicleListItemDto[]>) {
            state.vehicles = action.payload;
        },
        setPositions(state, action: PayloadAction<VehiclePositionInfo[]>) {
            state.positions = action.payload;
        },
        setLoading(state, action: PayloadAction<boolean>) {
            state.loading = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchVehicleTripSegments.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchVehicleTripSegments.fulfilled, (state, action) => {
                state.loading = false;
                state.vehicleRoute = action.payload;
                state.mapData = transformVehicleTripSegments(action.payload);
            })
            .addCase(fetchVehicleTripSegments.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const {
    setGroupMapPoints,
    setActiveTab,
    setIsWorkShiftView,
    toggleWorkShiftView,
    setIsVehicleRouteView,
    toggleVehicleRouteView,
    setSelectedVehicleId,
    setSelectedEmployeeId,
    setSelectedWorkShiftTimeSlotId,
    setSelectedWorkShift,
    setSelectedRouteId,
    setHoveredRouteId,
    setSidebarTitle,
    setVehicleRoute,
    setTripStartDate,
    setTripEndDate,
    setVehicles,
    setPositions,
    setLoading,
} = monitorViewSlice.actions;
export const monitoriViewReducer = monitorViewSlice.reducer;